import React, { useMemo } from "react";
import { useField } from "formik";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import { RuleGroupType } from "@common/types/transactionRule";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Button } from "@design-system/Button/Button";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { CloseIcon } from "@icons";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    TransactionRuleConditionFormSchema,
    FormRuleConditionType,
} from "../constants";
import styles from "../RuleForm.module.scss";
import { getSupportedRuleConditionTypes } from "./supportedRuleConditionTypes";
import { CategoryConditionForm } from "./CategoryConditionForm";
import { BankDescriptionConditionForm } from "./BankDescriptionConditionForm";
import { AmountConditionForm } from "./AmountConditionForm";
import { AccountConditionForm } from "./AccountConditionForm";
import { EntityConditionForm } from "./EntityConditionForm";
import { CounterpartyConditionForm } from "./CounterpartyConditionForm";
import { DateConditionForm } from "./DateConditionForm";
import { ClassConditionForm } from "./ClassConditionForm";
import { SimilarToConditionForm } from "./SimilarToConditionForm";

interface Props {
    shouldAutoFocus?: boolean;
    condition: TransactionRuleConditionFormSchema;
    index: number;
    onRemove: (id: string) => void;
    selectedConditionTypes: FormRuleConditionType[];
    groupType: RuleGroupType;
    /**
     * How the remove affordance is presented for conditions that can be
     * removed. "menu" (default) renders a `Menu.More` kebab with a
     * destructive "Remove" menu item; "inline" renders a compact X button
     * right after the condition's controls. Entity conditions are never
     * removable regardless of this prop.
     */
    removeTrigger?: "menu" | "inline";
    /**
     * How the condition type label ("Similar to", "Amount", ...) is presented.
     * "select" (default) renders the existing `Select.TriggerButton` dropdown
     * so the user can change the condition type; "plain" renders the label as
     * inline gray text (same style as the table header) for compact readonly
     * layouts.
     */
    labelStyle?: "select" | "plain";
    labelClassName?: string;
    /**
     * Condition types that are always non-removable regardless of the
     * `removeTrigger` mode. Entity conditions are always non-removable on
     * top of this list.
     */
    nonRemovableConditionTypes?: FormRuleConditionType[];
    controlButtonSize?: TriggerButtonProps["size"];
    allowedAmountIsCredit?: boolean;
    amountDirectionTagClassName?: string;
    normalizedDescriptions?: NormalizedDescriptionInfo[];
}

export const RuleConditionForm: React.FC<Props> = ({
    condition,
    shouldAutoFocus = false,
    index,
    onRemove,
    selectedConditionTypes,
    groupType,
    removeTrigger = "menu",
    labelStyle = "select",
    labelClassName,
    nonRemovableConditionTypes,
    controlButtonSize,
    allowedAmountIsCredit,
    amountDirectionTagClassName,
    normalizedDescriptions,
}) => {
    const { enableSimilarityRules } = useTypedFlags();
    const { enabled: isGlFirst } = useIsGlFirst();
    const isClassesEnabled = useIsClassesEnabled();
    const supportedRuleConditionTypes = useMemo(
        () => getSupportedRuleConditionTypes({ isClassesEnabled }),
        [isClassesEnabled],
    );
    const [conditionTypeField, , conditionTypeHelpers] = useField<
        TransactionRuleConditionFormSchema["conditionType"]
    >(`conditions.${index}.conditionType`);

    const canRemove =
        condition.conditionType !== FormRuleConditionType.ENTITY &&
        !(
            condition.conditionType !== undefined &&
            nonRemovableConditionTypes?.includes(condition.conditionType)
        );
    const conditionType = conditionTypeField.value;

    const conditionTypesOptions = useMemo(
        () =>
            Object.values(FormRuleConditionType)
                .filter(
                    (type) =>
                        // GL-first workspaces classify by general ledger, so
                        // category conditions are never selectable as a new
                        // option. Keep the category option when it's already
                        // the condition's selected type so pre-existing rules
                        // remain visible and editable.
                        !(
                            isGlFirst &&
                            type === FormRuleConditionType.CATEGORY &&
                            condition.conditionType !==
                                FormRuleConditionType.CATEGORY
                        ) &&
                        ((supportedRuleConditionTypes.includes(type) &&
                            !selectedConditionTypes.includes(type) &&
                            (type !== FormRuleConditionType.SIMILAR_TO ||
                                enableSimilarityRules)) ||
                            type === condition.conditionType),
                )
                .map((value) => {
                    const isDisabled = false;

                    if (value === FormRuleConditionType.COUNTERPARTY) {
                        return {
                            value,
                            label: "Counterparty",
                            disabled: isDisabled,
                        };
                    }

                    return {
                        value,
                        label: value,
                        disabled: isDisabled,
                    };
                }),
        [
            condition.conditionType,
            selectedConditionTypes,
            enableSimilarityRules,
            isGlFirst,
            supportedRuleConditionTypes,
        ],
    );

    const labelText = conditionTypesOptions.find(
        (o) => o.value === conditionType,
    )?.label;

    return (
        <>
            <div className={styles.sectionRowContent}>
                {conditionType !== FormRuleConditionType.ENTITY &&
                    labelStyle === "plain" && (
                        <span className={labelClassName}>{labelText}</span>
                    )}
                {conditionType !== FormRuleConditionType.ENTITY &&
                    labelStyle === "select" && (
                        <Picker.Select
                            items={conditionTypesOptions}
                            value={conditionType ?? null}
                            onValueChange={(value) => {
                                if (value !== null) {
                                    conditionTypeHelpers.setValue(value);
                                }
                            }}
                            placeholder="Select"
                            trigger={
                                <Picker.Button
                                    autoFocus={shouldAutoFocus}
                                    variant="primary"
                                    size={controlButtonSize ?? "md"}
                                />
                            }
                        />
                    )}

                {conditionType === FormRuleConditionType.CATEGORY && (
                    <CategoryConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.SUGGESTED_CATEGORY && (
                    <CategoryConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.BANK_DESCRIPTION && (
                    <BankDescriptionConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.AMOUNT && (
                    <AmountConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                        allowedAmountIsCredit={allowedAmountIsCredit}
                        amountDirectionTagClassName={
                            amountDirectionTagClassName
                        }
                    />
                )}
                {conditionType === FormRuleConditionType.DATE && (
                    <DateConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.ACCOUNT && (
                    <AccountConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.ENTITY && (
                    <EntityConditionForm index={index} groupType={groupType} />
                )}
                {conditionType === FormRuleConditionType.COUNTERPARTY && (
                    <CounterpartyConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.CLASS && (
                    <ClassConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}
                {conditionType === FormRuleConditionType.SIMILAR_TO && (
                    <SimilarToConditionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                        normalizedDescriptions={normalizedDescriptions}
                    />
                )}
            </div>

            {canRemove && removeTrigger === "menu" && (
                <Menu.More>
                    <Menu.Item
                        onClick={() => onRemove(condition.id)}
                        destructive
                    >
                        Remove
                    </Menu.Item>
                </Menu.More>
            )}
            {canRemove && removeTrigger === "inline" && (
                <Button
                    iconOnly
                    variant="secondary"
                    size={controlButtonSize ?? "sm"}
                    type="button"
                    aria-label="Remove condition"
                    onClick={() => onRemove(condition.id)}
                >
                    <CloseIcon />
                </Button>
            )}
        </>
    );
};
